/**
 * BollaScanButton — Pulsante scansione barcode/QR nel form Bolla.
 * Apre un mini-scanner overlay: quando la pistola legge un codice, cerca il
 * materiale nel Magazzino e pre-compila i campi della bolla:
 *   - materiale (nome)
 *   - unita_misura
 *   - prezzo_unitario (se ruolo autorizzato + prezzo presente)
 *   - categoria (mappata dalla categoria magazzino)
 *
 * Non modifica giacenze: la bolla poi verrà salvata normalmente.
 */
import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ScanLine, X, Package, RefreshCw, AlertTriangle, CheckCircle2 } from "lucide-react";
import api from "@/api";

// Mappa categoria Magazzino → categoria Bolla (le prime sono compatibili)
const CATEGORIA_MAP = {
  edile: "Materiali edili",
  elettrico: "Elettrici",
  idraulico: "Idrici",
  generico: "Materiali",
};

function beep(freq = 880, ms = 90) {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.frequency.value = freq;
    osc.type = "sine";
    g.gain.value = 0.15;
    osc.connect(g).connect(ctx.destination);
    osc.start();
    setTimeout(() => osc.stop(), ms);
  } catch { /* silent */ }
}

export default function BollaScanButton({ onFill, canSeePrices = true }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="btn-scan-bolla"
        className="inline-flex items-center gap-2 px-3 py-2 bg-cyan-700 hover:bg-cyan-800 text-white text-xs font-bold rounded-sm shadow-sm"
        title="Scansiona un materiale dal Magazzino per compilare la bolla"
      >
        <ScanLine className="w-4 h-4" />
        Scansiona materiale
      </button>
      {open && (
        <ScanOverlay
          onClose={() => setOpen(false)}
          onFill={(mat) => {
            onFill(mat);
            setOpen(false);
          }}
          canSeePrices={canSeePrices}
        />
      )}
    </>
  );
}

function ScanOverlay({ onClose, onFill, canSeePrices }) {
  const [buf, setBuf] = useState("");
  const [lastFound, setLastFound] = useState(null);
  const [notFound, setNotFound] = useState(null);
  const [lastScan, setLastScan] = useState(null);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    const focus = () => inputRef.current && inputRef.current.focus();
    focus();
    const onBlur = () => setTimeout(focus, 50);
    inputRef.current && inputRef.current.addEventListener("blur", onBlur);
    return () => inputRef.current && inputRef.current.removeEventListener("blur", onBlur);
  }, []);

  const handleScan = async (raw) => {
    const code = (raw || "").trim();
    if (!code) return;
    if (lastScan && lastScan.code === code && Date.now() - lastScan.ts < 400) {
      setBuf("");
      return;
    }
    setLastScan({ code, ts: Date.now() });
    setBuf("");
    setNotFound(null);
    setLoading(true);
    try {
      const { data: mat } = await api.get(
        `/magazzino/materiali/by-barcode/${encodeURIComponent(code)}`
      );
      const patch = {
        materiale: mat.nome,
        unita_misura: mat.unita_misura || "pz",
        categoria: CATEGORIA_MAP[mat.categoria] || "Materiali",
      };
      if (canSeePrices && mat.prezzo_unitario != null) {
        patch.prezzo_unitario = String(mat.prezzo_unitario).replace(".", ",");
      }
      setLastFound({ ...mat, code_scan: code });
      beep(1200, 70);
      toast.success(`${mat.nome} — dati precompilati`);
      // Auto-fill dopo mini pausa per far vedere il feedback (300ms)
      setTimeout(() => onFill(patch), 350);
    } catch (e) {
      if (e?.response?.status === 404) {
        beep(220, 200);
        setNotFound(code);
      } else {
        beep(220, 200);
        toast.error(e?.response?.data?.detail || e.message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] bg-slate-900/90 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="bolla-scan-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full max-w-lg rounded-md shadow-2xl overflow-hidden"
      >
        <div className="bg-slate-900 text-white p-4 flex items-center gap-3">
          <ScanLine className="w-6 h-6 text-cyan-400" />
          <div className="flex-1">
            <div className="overline text-cyan-400">Bolla · Precompila da scansione</div>
            <div className="font-display font-bold">Scansiona barcode / QR del materiale</div>
          </div>
          <button onClick={onClose} data-testid="btn-close-bolla-scan" className="p-2 hover:bg-slate-800 rounded-sm">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 text-center">
          <div className="w-20 h-20 mx-auto rounded-full bg-cyan-100 flex items-center justify-center mb-4">
            {loading ? (
              <RefreshCw className="w-10 h-10 text-cyan-600 animate-spin" />
            ) : (
              <ScanLine className="w-10 h-10 text-cyan-600" />
            )}
          </div>
          <div className="font-display font-black text-xl text-slate-900 mb-1">
            {loading ? "Ricerca in corso…" : "In attesa di scansione…"}
          </div>
          <div className="text-slate-500 text-xs">
            Punta la pistola sul codice del materiale — al beep i campi della bolla vengono precompilati.
          </div>
          <input
            ref={inputRef}
            value={buf}
            onChange={(e) => setBuf(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleScan(buf);
              }
            }}
            data-testid="bolla-scan-input"
            autoComplete="off"
            data-no-uppercase
            className="absolute opacity-0 pointer-events-none"
            aria-label="Campo scansione bolla"
          />
        </div>

        {lastFound && (
          <div className="mx-6 mb-4 p-3 bg-emerald-50 border border-emerald-300 rounded-sm text-xs text-emerald-900 flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
            <div>
              <div className="font-bold">{lastFound.nome}</div>
              <div className="font-mono text-[10px] opacity-70 mt-0.5">
                {lastFound.code_scan} · {lastFound.unita_misura}
                {canSeePrices && lastFound.prezzo_unitario != null && ` · ${lastFound.prezzo_unitario} €`}
              </div>
            </div>
          </div>
        )}

        {notFound && (
          <div className="mx-6 mb-4 p-3 bg-rose-50 border border-rose-300 rounded-sm text-xs text-rose-900 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <div>
              <div className="font-bold">Codice non trovato</div>
              <div className="font-mono text-[10px] opacity-70 mt-0.5">{notFound}</div>
              <div className="mt-1">Aggiungi il barcode al materiale in Magazzino per riconoscerlo.</div>
            </div>
          </div>
        )}

        <div className="px-6 pb-4 flex justify-end">
          <button
            onClick={onClose}
            data-testid="btn-annulla-bolla-scan"
            className="text-xs px-3 py-1.5 border border-slate-300 hover:bg-slate-50 rounded-sm"
          >
            Chiudi
          </button>
        </div>
      </div>
    </div>
  );
}
